import { memo, useState } from "react";

import { Trans } from "@similarweb/i18n-provider";
import { useDispatch, useSelector } from "react-redux";

import { Button } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import {
    StrongText,
    StyledModalActions,
    StyledModalContent,
    StyledModalTitle,
    StyledRadioButton,
    SubscriptionRadioButtonLabel,
} from "app/pages/benchmark-tracker/components/Report/SubscriptionModal/styles";
import { generateAutomationName } from "app/pages/benchmark-tracker/helpers/helpers";
import { BenchmarkGranularity } from "app/pages/benchmark-tracker/services/api/constants";
import { benchmarkTrackerTracking } from "app/pages/benchmark-tracker/services/tracking";
import { closeSubscriptionModal } from "app/pages/benchmark-tracker/store/actions/report";
import { subscribeToDigestEffect } from "app/pages/benchmark-tracker/store/effects/menu";
import { selectSelectedBenchmark } from "app/pages/benchmark-tracker/store/selectors/entitySelectors";
import {
    selectDigestGranularity,
    selectIsDigestSubscribed,
    selectIsSubscriptionModalLoading,
} from "app/pages/benchmark-tracker/store/selectors/reportSelectors";
import { proNavigator } from "single-spa/react-app/ProNavigator";

interface SubscribeSettingsContentProps {
    onUnsubscribe: () => void;
    isViewerMode: boolean;
}

export const SubscribeSettingsContent = memo(({ onUnsubscribe, isViewerMode }: SubscribeSettingsContentProps) => {
    const selectedBenchmark = useSelector(selectSelectedBenchmark);

    const subscribedFrequency = useSelector(selectDigestGranularity);
    const { name } = selectedBenchmark ?? {};
    const { key: benchmarkId } = proNavigator.getParams();
    const isDigestSubscribed = useSelector(selectIsDigestSubscribed);
    const [frequency, setFrequency] = useState(subscribedFrequency);

    const dispatch = useDispatch();
    const isSubscribeLoading = useSelector(selectIsSubscriptionModalLoading);
    const email = swSettings.user?.username;
    const track = benchmarkTrackerTracking.report;

    const closeModal = () => {
        dispatch(closeSubscriptionModal());
        track.cancelSubscriptionModal(!isViewerMode);
    };

    const onSubscribe = () => {
        dispatch(subscribeToDigestEffect(benchmarkId, email ?? "", name ?? "", frequency));
        track.confirmSubscriptionModal(frequency, !isViewerMode);
    };

    const onFrequencyChange = (frequency: BenchmarkGranularity) => () => {
        setFrequency(frequency);
    };

    const unsubscribeHandler = () => {
        onUnsubscribe();
        track.unsubscribeFromDigest(!isViewerMode);
    };

    const saveButtonText = isDigestSubscribed ? "bt.report.modal.save.button.text" : "bt.menu.subscribe";

    return (
        <>
            <StyledModalTitle
                data-automation={generateAutomationName("report", ["subscription-modal", "subscribe", "title"])}
            >
                {translate("bt.report.modal.subscribe.title")}
            </StyledModalTitle>

            <StyledModalContent
                data-automation={generateAutomationName("report", ["subscription-modal", "subscribe", "text"])}
            >
                <Trans
                    i18nKey={"bt.report.modal.subscribe.text"}
                    components={{ strong: <StrongText /> }}
                    values={{ email }}
                />

                <SubscriptionRadioButtonLabel>{translate("bt.report.frequency.text")}</SubscriptionRadioButtonLabel>

                <Flex
                    width="100%"
                    gap="16px"
                >
                    <StyledRadioButton
                        itemLabel={translate("bt.report.modal.subscription.frequency.weekly")}
                        itemSelected={frequency === BenchmarkGranularity.WEEKLY}
                        onClick={onFrequencyChange(BenchmarkGranularity.WEEKLY)}
                    />
                    <StyledRadioButton
                        itemLabel={translate("bt.report.modal.subscription.frequency.monthly")}
                        itemSelected={frequency === BenchmarkGranularity.MONTHLY}
                        onClick={onFrequencyChange(BenchmarkGranularity.MONTHLY)}
                    />
                </Flex>
            </StyledModalContent>

            <StyledModalActions>
                {isDigestSubscribed ? (
                    <Button
                        type="flatDark"
                        onClick={unsubscribeHandler}
                        dataAutomation={generateAutomationName("report", [
                            "subscription-modal",
                            "unsubscribe",
                            "button",
                        ])}
                        className="unsubscribeButton"
                    >
                        {translate("bt.menu.unsubscribe")}
                    </Button>
                ) : null}

                <Button
                    type="flat"
                    onClick={closeModal}
                    dataAutomation={generateAutomationName("report", ["subscription-modal", "cancel", "button"])}
                >
                    {translate("global.cancel")}
                </Button>
                <Button
                    onClick={onSubscribe}
                    isLoading={isSubscribeLoading}
                    dataAutomation={generateAutomationName("report", ["subscription-modal", "subscribe", "button"])}
                >
                    {translate(saveButtonText)}
                </Button>
            </StyledModalActions>
        </>
    );
});

SubscribeSettingsContent.displayName = "SubscribeSettingsContent";
